import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';
/**
 * Knowledge Packs V3 API Client
 * ================================
 * API functions for interacting with V3 Knowledge Pack endpoints
 * 
 * Features:
 * - Modular pack CRUD
 * - Relationship validation
 * - Version management
 * - Graph data export
 * - Analytics and statistics
 */

// ============================================================
// PACK CRUD OPERATIONS
// ============================================================

/**
 * Inject required identity fields & normalise legacy `metadata` → `metadata_overrides`
 * so callers don't have to remember backend contract details. Backend
 * `CreatePackV3Request` requires `created_by` at ROOT, and stores status at
 * `metadata.status` via `metadata_overrides`.
 */
const _currentUserIdentifier = () => {
  try {
    const userJson = secureStorage.getItem('nexus_user');
    if (userJson) {
      const u = JSON.parse(userJson);
      return u.email || u.user_id || 'management';
    }
  } catch (_e) { /* fallthrough */ }
  return 'management';
};

const _normalizePayload = (packData, { isUpdate = false } = {}) => {
  const out = { ...packData };
  const who = _currentUserIdentifier();
  if (isUpdate) {
    if (!out.updated_by) out.updated_by = who;
    if (!out.change_summary) out.change_summary = 'Updated';
  } else {
    if (!out.created_by) out.created_by = who;
  }
  // If caller put `metadata` at root and didn't already provide
  // metadata_overrides, lift it. Backend ignores root `metadata`.
  if (out.metadata && !out.metadata_overrides) {
    out.metadata_overrides = out.metadata;
    delete out.metadata;
  }
  return out;
};

/**
 * Create a new Knowledge Pack V3
 */
export const createPackV3 = async (packData) => {
  const response = await fetch(`${BACKEND_URL}/api/v3/packs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    },
    body: JSON.stringify(_normalizePayload(packData)),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    // FastAPI 422 envelope: { code, message, errors: [{loc, msg}] }
    let msg = error.detail;
    if (!msg && Array.isArray(error.errors) && error.errors.length) {
      const f = error.errors[0];
      const field = Array.isArray(f.loc) ? f.loc.join('.') : (f.loc || 'field');
      msg = `${error.message || 'Validation failed'} — ${field}: ${f.msg || 'invalid'}`;
    }
    throw new Error(msg || error.message || 'Failed to create pack');
  }

  return response.json();
};

/**
 * List Knowledge Packs V3 with advanced filtering
 */
export const listPacksV3 = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.pack_type) params.append('pack_type', filters.pack_type);
  if (filters.status) params.append('status', filters.status);
  if (filters.visibility) params.append('visibility', filters.visibility);
  if (filters.studio) params.append('studio', filters.studio);
  if (filters.tags) params.append('tags', filters.tags.join(','));
  if (filters.search) params.append('search', filters.search);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.skip) params.append('skip', filters.skip);

  const response = await fetch(`${BACKEND_URL}/api/v3/packs?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to list packs');
  }

  return response.json();
};

/**
 * Get a single Knowledge Pack V3 by ID
 */
export const getPackV3 = async (packId, options = {}) => {
  const params = new URLSearchParams();
  
  if (options.expand_relationships) params.append('expand_relationships', 'true');
  if (options.include_version_history) params.append('include_version_history', 'true');

  const response = await fetch(`${BACKEND_URL}/api/v3/packs/${packId}?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get pack');
  }

  return response.json();
};

/**
 * Update a Knowledge Pack V3
 */
export const updatePackV3 = async (packId, updateData) => {
  const response = await fetch(`${BACKEND_URL}/api/v3/packs/${packId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    },
    body: JSON.stringify(_normalizePayload(updateData, { isUpdate: true })),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    let msg = error.detail;
    if (!msg && Array.isArray(error.errors) && error.errors.length) {
      const f = error.errors[0];
      const field = Array.isArray(f.loc) ? f.loc.join('.') : (f.loc || 'field');
      msg = `${error.message || 'Validation failed'} — ${field}: ${f.msg || 'invalid'}`;
    }
    throw new Error(msg || error.message || 'Failed to update pack');
  }

  return response.json();
};

/**
 * Delete (Archive) a Knowledge Pack V3
 */
export const deletePackV3 = async (packId) => {
  const response = await fetch(`${BACKEND_URL}/api/v3/packs/${packId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete pack');
  }

  return response.json();
};

// ============================================================
// RELATIONSHIP OPERATIONS
// ============================================================

/**
 * Validate a proposed relationship between packs
 */
export const validateRelationship = async (sourcePackId, targetPackId, relationshipType) => {
  const response = await fetch(`${BACKEND_URL}/api/v3/relationships/validate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    },
    body: JSON.stringify({
      source_pack_id: sourcePackId,
      target_pack_id: targetPackId,
      relationship_type: relationshipType,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to validate relationship');
  }

  return response.json();
};

// ============================================================
// VERSION OPERATIONS
// ============================================================

/**
 * Get version history for a pack
 */
export const getVersionHistory = async (packId) => {
  const response = await fetch(`${BACKEND_URL}/api/v3/packs/${packId}/versions`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get version history');
  }

  return response.json();
};

/**
 * Compare two pack versions
 */
export const compareVersions = async (packId, version1, version2) => {
  const response = await fetch(
    `${BACKEND_URL}/api/v3/packs/${packId}/versions/compare?v1=${version1}&v2=${version2}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to compare versions');
  }

  return response.json();
};

// ============================================================
// GRAPH & VISUALIZATION
// ============================================================

/**
 * Get graph data for visualization
 */
export const getGraphData = async (packTypes = null, includeArchived = false) => {
  const params = new URLSearchParams();
  
  if (packTypes) params.append('pack_types', packTypes.join(','));
  if (includeArchived) params.append('include_archived', 'true');

  const response = await fetch(`${BACKEND_URL}/api/v3/graph/export?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get graph data');
  }

  return response.json();
};

// ============================================================
// ANALYTICS & STATISTICS
// ============================================================

/**
 * Get pack analytics
 */
export const getPackAnalytics = async (packId, days = 30) => {
  const response = await fetch(
    `${BACKEND_URL}/api/v3/analytics/pack/${packId}?days=${days}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get analytics');
  }

  return response.json();
};

/**
 * Get global pack statistics
 */
export const getPackStatistics = async () => {
  const response = await fetch(`${BACKEND_URL}/api/v3/analytics/statistics`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get statistics');
  }

  return response.json();
};

// ============================================================
// BULK OPERATIONS
// ============================================================

/**
 * Bulk activate packs
 */
export const bulkActivatePacks = async (packIds) => {
  const response = await fetch(`${BACKEND_URL}/api/v3/bulk/activate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    },
    body: JSON.stringify({ pack_ids: packIds }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to activate packs');
  }

  return response.json();
};

/**
 * Bulk deactivate packs
 */
export const bulkDeactivatePacks = async (packIds) => {
  const response = await fetch(`${BACKEND_URL}/api/v3/bulk/deactivate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    },
    body: JSON.stringify({ pack_ids: packIds }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to deactivate packs');
  }

  return response.json();
};

// ============================================================
// REGISTRY SUMMARY (for Dashboard)
// ============================================================

/**
 * Get pack registry summary for dashboard
 */
export const getRegistrySummary = async () => {
  const response = await fetch(`${BACKEND_URL}/api/v3/registry/summary`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get registry summary');
  }

  return response.json();
};

// ============================================================
// CONSTANTS & HELPERS
// ============================================================

export const PACK_TYPES = {
  PRODUCT: 'product',
  PROMOTION: 'promotion',
  DIRECTION: 'direction',
  VALUES: 'values',
  SERVICES: 'services',
  COMPLIANCE: 'compliance',
  VIRAL: 'viral',
  TREND: 'trend',
  AMBASSADOR: 'ambassador',
  HEALTH: 'health',
  FORMAT: 'format',
};

export const PACK_STATUS = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  ARCHIVED: 'archived',
};

export const VISIBILITY = {
  GLOBAL: 'global',
  STUDIO_SPECIFIC: 'studio_specific',
  GROUP_SPECIFIC: 'group_specific',
};

export const RELATIONSHIP_TYPES = {
  REQUIRES: 'requires',
  EXTENDS: 'extends',
  CONFLICTS_WITH: 'conflicts_with',
  COMPLEMENTS: 'complements',
  SUPERSEDES: 'supersedes',
};

export const getPackTypeIcon = (type) => {
  const icons = {
    product: '📦',
    promotion: '🎁',
    objection: '🛡️',
    competitor: '⚔️',
    compliance: '✅',
    process: '⚙️',
    communication: '💬',
    integration: '🔗',
  };
  return icons[type] || '📄';
};

export const getPackStatusColor = (status) => {
  const colors = {
    draft: 'bg-gray-100 text-gray-600',
    published: 'bg-green-100 text-green-700',
    archived: 'bg-red-100 text-red-600',
  };
  return colors[status] || 'bg-gray-100 text-gray-600';
};

export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-MY', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// ============================================================
// DRAFT OPERATIONS
// ============================================================

/**
 * Save pack as draft
 */
export const saveDraftV3 = async (draftData) => {
  const response = await fetch(`${BACKEND_URL}/api/v3/registry/drafts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    },
    body: JSON.stringify(draftData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to save draft');
  }

  return response.json();
};

/**
 * Get draft by temp_id
 */
export const getDraftV3 = async (tempId) => {
  const response = await fetch(`${BACKEND_URL}/api/v3/registry/drafts/${tempId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get draft');
  }

  return response.json();
};

/**
 * Delete draft
 */
export const deleteDraftV3 = async (tempId) => {
  const response = await fetch(`${BACKEND_URL}/api/v3/registry/drafts/${tempId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete draft');
  }

  return response.json();
};
